import { ref, shallowRef } from 'vue'
import { useTimeoutFn } from '@vueuse/core'
import { defineStore } from 'pinia'

import useModalStore from '@/stores/modal.ts'
import useGravityGatewayStore from '@/stores/gravityGateway.ts'
import useAuthStore from '@/stores/auth.ts'
import { type SSBTButtonEventDetail, SSBTButtons } from '@/types/events.ts'

const useSSBTEventMonitorStore = defineStore('ssbt-event-monitor', () => {
  const gravityGatewayStore = useGravityGatewayStore()
  const modalStore = useModalStore()
  const authStore = useAuthStore()

  const isInactivityTimeoutActive = ref(false)
  const stopUserInactivityTimeout = shallowRef(() => {})
  let startUserInactivityTimeout = () => {}
  let restartUserInactivityTimeoutCallback = () => {}

  const restartUserInactivityTimeout = () => {
    startUserInactivityTimeout()
    restartUserInactivityTimeoutCallback()
  }

  const initUserInactivityTimeout = (
    duration: number,
    timeoutCallback: () => void,
    restartTimeoutCallback: () => void = () => {}
  ) => {
    const { start, stop } = useTimeoutFn(timeoutCallback, duration, { immediate: false })

    startUserInactivityTimeout = () => {
      isInactivityTimeoutActive.value = true
      window.addEventListener('pointerup', restartUserInactivityTimeout)
      start()
    }
    stopUserInactivityTimeout.value = () => {
      isInactivityTimeoutActive.value = false
      window.removeEventListener('pointerup', restartUserInactivityTimeout)
      stop()
    }
    restartUserInactivityTimeoutCallback = restartTimeoutCallback

    startUserInactivityTimeout()
  }

  const initSSBTButtonsListener = () => {
    window.addEventListener('adpkeyevent', (event: CustomEvent<SSBTButtonEventDetail>) => {
      if (isInactivityTimeoutActive.value) restartUserInactivityTimeout()

      switch (event.detail.keydown) {
        case SSBTButtons.Menu:
          modalStore.closeAllNonPreservedModals()

          gravityGatewayStore.notifyRouteChanged({
            params: {
              param1: 'all',
              param2: ''
            }
          })
          break
        case SSBTButtons.Cashout:
          if (!authStore.isAuthenticated) return

          modalStore.modalState.logout = true
          break
      }
    })
  }

  return {
    initSSBTButtonsListener,
    initUserInactivityTimeout,
    startUserInactivityTimeout,
    stopUserInactivityTimeout,
    restartUserInactivityTimeout
  }
})

export default useSSBTEventMonitorStore
